

import React from 'react';
import { UserRole, UserProfile, AgencyClient } from '../types';
import { Button, Card } from '../components/UI';
import { Users } from 'lucide-react';

interface SettingsProps {
  currentUser: UserProfile;
  currentRole: UserRole;
  clients: AgencyClient[];
  onSwitchRole: (role: UserRole) => void;
  onSelectClient: (id: string) => void;
}

export const SettingsPanel: React.FC<SettingsProps> = ({ 
  currentUser, 
  currentRole, 
  clients,
  onSwitchRole, 
  onSelectClient
}) => {
  const isAdmin = currentUser.role === UserRole.ADMIN;
  const isAgency = currentUser.role === UserRole.AGENCY || isAdmin;

  return (
    <div className="fixed inset-0 z-50 bg-brand-text/5 backdrop-blur-sm flex justify-end">
      <div className="w-full max-w-md bg-white h-full shadow-2xl p-6 overflow-y-auto animate-slideIn">
        <div className="flex justify-between items-center mb-8">
           <h2 className="text-2xl font-bold">Control Center</h2>
           <Button variant="ghost" onClick={() => onSwitchRole(currentRole)}>Close</Button>
        </div>

        {/* Role Switcher (For Admin/Dev) */}
        {isAdmin && (
          <Card className="mb-6 bg-gray-50">
            <h3 className="text-sm font-bold uppercase mb-4 text-gray-500">Admin Override</h3>
            <div className="flex gap-2">
              <Button 
                variant={currentRole === UserRole.ADMIN ? 'primary' : 'outline'} 
                onClick={() => onSwitchRole(UserRole.ADMIN)} 
                className="flex-1 text-xs"
              >
                Admin
              </Button>
              <Button 
                variant={currentRole === UserRole.AGENCY ? 'primary' : 'outline'} 
                onClick={() => onSwitchRole(UserRole.AGENCY)} 
                className="flex-1 text-xs"
              >
                Agency
              </Button>
              <Button 
                variant={currentRole === UserRole.USER ? 'primary' : 'outline'} 
                onClick={() => onSwitchRole(UserRole.USER)} 
                className="flex-1 text-xs"
              >
                User
              </Button>
            </div>
          </Card>
        )}

        {/* Agency Client Management */}
        {isAgency && currentRole === UserRole.AGENCY && (
          <div className="space-y-6">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <Users size={20} /> Client Roster
            </h3>
            
            <div className="bg-blue-50 p-4 rounded-xl text-xs text-blue-600 mb-4">
               Clients are now managed via the central API dashboard. Use this list to switch contexts.
            </div>

            <div className="space-y-3">
              {clients.map(client => (
                <div 
                  key={client.id} 
                  className="flex justify-between items-center p-4 border rounded-xl hover:bg-gray-50 cursor-pointer group"
                  onClick={() => onSelectClient(client.id)}
                >
                  <div>
                    <h4 className="font-bold">{client.companyName}</h4>
                    <p className="text-xs text-gray-500">{client.assets.length} Assets</p>
                  </div>
                  <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100">Manage</Button>
                </div>
              ))}
              {clients.length === 0 && <p className="text-center text-gray-400 py-4">No clients assigned.</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
